import { format } from "date-fns";
import { BookingState } from "@/types/bookinghome";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { createBooking } from "@/utils/endUserAppApi";
import { ActivityDetail } from "@/types/activitydetail";
import { PricingResponse } from "@/types/pricing";
import { useState } from "react";


interface BookingSummaryProps {
  bookingState: BookingState;
  className?: string;
  navigate: (path: string, options?: { state?: any }) => void;
  activity: ActivityDetail;
  pricing?: PricingResponse['pricing'];
  isProcessing?: boolean;
}

const BookingSummary = ({ 
  bookingState, 
  className, 
  navigate, 
  activity,
  pricing,
  isProcessing = false 
}: BookingSummaryProps) => {
  const { selectedDate, selectedTimeSlot, participants } = bookingState;
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const calculateTotal = () => {
    if (!selectedTimeSlot || !pricing) return 0;
    
    return (
      participants.adults * pricing.adults.amount +
      participants.children * pricing.children.amount +
      participants.students * pricing.students.amount
    );
  };


  const totalParticipants =
    participants.adults + participants.children + participants.students;

  const isBookingComplete =
    selectedDate &&
    selectedTimeSlot &&
    totalParticipants > 0 &&
    customerDetails.name &&
    customerDetails.email;

  return (
    <Card className={cn("p-6", className)}>
      <h2 className="text-xl font-semibold mb-4">Booking Summary</h2>

      <div className="space-y-4">
        <div>
          <div className="text-sm text-muted-foreground">Date</div>
          <div className="font-medium">
            {selectedDate ? format(selectedDate, "MMMM d, yyyy") : "Not selected"}
          </div>
        </div>

        <div>
          <div className="text-sm text-muted-foreground">Time</div>
          <div className="font-medium">
            {selectedTimeSlot
              ? `${selectedTimeSlot.startTime} - ${selectedTimeSlot.endTime}`
              : "Not selected"}
          </div>
        </div>

        {selectedTimeSlot && totalParticipants > 0 && (
          <>
            <div className="border-t pt-4">
              <h3 className="font-medium mb-2">Price Breakdown</h3>
              {participants.adults > 0 && (
                <div className="flex justify-between text-sm">
                  <span>Adults (x{participants.adults})</span>
                  <span>${participants.adults * selectedTimeSlot.price.adult.amount}</span>
                </div>
              )}
              {participants.children > 0 && (
                <div className="flex justify-between text-sm">
                  <span>Children (x{participants.children})</span>
                  <span>
                  ${participants.children * selectedTimeSlot.price.child.amount}
                  </span>
                </div>
              )}
              {participants.students > 0 && (
                <div className="flex justify-between text-sm">
                  <span>Students (x{participants.students})</span>
                  <span>
                  ${participants.students * selectedTimeSlot.price.student.amount}
                  </span>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span>${calculateTotal()}</span>
              </div>
            </div>
          </>
        )}

<div className="border-t pt-4">
  <h3 className="font-medium mb-2">Customer Details</h3>
  <div className="space-y-3">
    <input
      type="text"
      placeholder="Full Name"
      className="w-full p-2 border rounded"
      value={customerDetails.name}
      onChange={(e) => setCustomerDetails(prev => ({...prev, name: e.target.value}))}
      required
    />
    <input
      type="email"
      placeholder="Email"
      className="w-full p-2 border rounded"
      value={customerDetails.email}
      onChange={(e) => setCustomerDetails(prev => ({...prev, email: e.target.value}))}
      required
    />
    <input
      type="tel"
      placeholder="Phone (optional)"
      className="w-full p-2 border rounded"
      value={customerDetails.phone}
      onChange={(e) => setCustomerDetails(prev => ({...prev, phone: e.target.value}))}
    />
  </div>
</div>

        <Button
            className="w-full mt-6"
            size="lg"
            disabled={!isBookingComplete || isProcessing}
            onClick={async () => {
              if (isBookingComplete) {
                try {
                  const bookingResponse = await createBooking({
                    activityId: activity._id,
                    selectedDate: selectedDate,
                    selectedTimeSlot: selectedTimeSlot.startTime,
                    participants: {
                      adult: participants.adults,
                      child: participants.children,
                      student: participants.students
                    },
                    customerDetails: {
                      name: customerDetails.name,
                      email: customerDetails.email,
                      phone: customerDetails.phone || undefined
                    },
                    specialRequests: ''
                  });
                  
            // Add logging to see the response
            console.log('Booking response:', bookingResponse);

                  navigate('/payment', { 
                    state: { 
                      bookingState: {
                        ...bookingState,
                        bookingId: bookingResponse.bookingId,
                        activityDetails: {
                          title: activity.name,
                          businessName: bookingResponse.businessName
                        }
                      }
                    } 
                  });
                } catch (error: any) {
                  toast({
                    title: "Booking Failed",
                    description: error.response?.data?.error || "Unable to create booking. Please try again.",
                    variant: "destructive",
                  });
                }
              }
            }}
          >
            {isBookingComplete ? "Proceed to Payment" : "Complete Your Booking"}
            
          </Button>
      </div>
    </Card>
  );
};

export default BookingSummary;